import { REHYDRATE } from 'redux-persist';
import type { UrgentMessage } from '../../common/graphql/fragments/gql/UrgentMessage';
import type { RehydrateActions } from '../../store/rehydrateActionTypes';
import type { StoreState } from '../../store/storeState';
import type { NotificationResult, NotificationsActions } from './notificationsActions';
import { removeOutdatedBookingResults } from './utils/removeOutdatedBookingResults';

export type CreatedAt = string;

export interface UrgentMessageNotification {
  createdAt: CreatedAt;
  message: UrgentMessage;
}

export interface BookingResultNotification {
  bookingResult: NotificationResult;
  createdAt: CreatedAt;
  id: number;
}

export type UrgentMessageNotificationRecord = Record<string, UrgentMessageNotification | undefined>;
export type BookingResultNotificationRecord = Record<number, BookingResultNotification | undefined>;

export type IdsRecord = Record<string, boolean | undefined>;

export interface NotificationsReducerState {
  bookingResults: BookingResultNotificationRecord;
  readUrgentMessageIds: IdsRecord;
  urgentMessage: UrgentMessageNotificationRecord;
}

const INITIAL_VALUE: NotificationsReducerState = {
  urgentMessage: {},
  bookingResults: {},
  readUrgentMessageIds: {},
};

export const notificationsReducer = (
  state: NotificationsReducerState = INITIAL_VALUE,
  action: NotificationsActions | RehydrateActions,
): NotificationsReducerState => {
  switch (action.type) {
    case REHYDRATE: {
      if (action.key === 'notifications-persisted-state' && action.payload) {
        return {
          ...state,
          bookingResults: removeOutdatedBookingResults(action.payload.bookingResults),
          readUrgentMessageIds: action.payload.readUrgentMessageIds,
        };
      }
      return state;
    }
    case 'NOTIFICATION:ADD_BOOKING_RESULT_NOTIFICATION': {
      const bookingResult = state.bookingResults[action.payload.id];
      if (bookingResult) {
        return state;
      }
      return {
        ...state,
        bookingResults: {
          ...state.bookingResults,
          [action.payload.id]: {
            bookingResult: action.payload.bookingResult,
            id: action.payload.id,
            createdAt: action.payload.createdAt,
          },
        },
      };
    }
    case 'NOTIFICATION:REMOVE_BOOKING_RESULT_NOTIFICATION': {
      const { [action.payload.id]: _item, ...rest } = state.bookingResults;
      return {
        ...state,
        bookingResults: rest,
      };
    }
    case 'NOTIFICATION:ADD_URGENT_MESSAGE_NOTIFICATION': {
      const urgentMessage = state.urgentMessage[action.payload.message.id];
      const isRead = state.readUrgentMessageIds[action.payload.message.id];

      if (urgentMessage && isRead) {
        return state;
      }

      return {
        ...state,
        urgentMessage: {
          ...state.urgentMessage,
          [action.payload.message.id]: {
            message: action.payload.message,
            createdAt: action.payload.createdAt,
          },
        },
      };
    }
    case 'NOTIFICATIONS:MARK_URGENT_MESSAGE_AS_UNREAD': {
      return {
        ...state,
        readUrgentMessageIds: {
          ...state.readUrgentMessageIds,
          [action.payload.id]: false,
        },
      };
    }
    case 'NOTIFICATIONS:MARK_URGENT_MESSAGE_AS_READ': {
      return {
        ...state,
        readUrgentMessageIds: {
          ...state.readUrgentMessageIds,
          [action.payload.id]: true,
        },
      };
    }
    default:
      return state;
  }
};

export const getNotifications = (state: StoreState): NotificationsReducerState =>
  state.notifications;

export const getUrgentMessageNotification = (state: StoreState): UrgentMessageNotificationRecord =>
  getNotifications(state).urgentMessage;
export const getBookingResultNotification = (state: StoreState): BookingResultNotificationRecord =>
  getNotifications(state).bookingResults;

export const getUrgentMessageNotificationById = (
  state: StoreState,
  notificationId: string,
): UrgentMessageNotification | undefined => getNotifications(state).urgentMessage[notificationId];

export const getUrgentMessageReadIds = (state: StoreState): IdsRecord =>
  getNotifications(state).readUrgentMessageIds;

export const getIsUrgentMessageRead = (state: StoreState, notificationId: string) =>
  Boolean(getUrgentMessageReadIds(state)[notificationId]);
